import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Task, TaskCompletion } from '../models';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { selectCurrentEmployee, selectTaskInEdit } from '../reducer';
import { TaskType, Interval } from '../models';
import { Input, requiredInput, checkVideoUrl } from '../../../core/formValidation';
import { ContentTabs } from '../../../core/components/ContentTabs';
import History from '../../taskDetail/components/History';
import TaskDetailNotes from '../../taskDetail/components/TaskDetailNotes';
import { TaskDetailsTab } from './TaskDetailsTab';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { generateToastr } from '../../../core/toastMessages';
import moment from 'moment';
import { IoClose } from 'react-icons/io5';
import { MdChevronLeft } from 'react-icons/md';
import { IoFlag } from 'react-icons/io5';
import { FaAngleDown } from 'react-icons/fa6';
import { TaskInstructionsTab } from './TaskInstructionsTab';
import { TaskMediaTab } from './TaskMediaTab';
import { TaskLinkedTab } from './TaskLinkedTab';
import {
  addTask,
  saveTask,
  getTask,
  deleteTask,
  deleteTaskInEdit,
  addEssentialTask,
  saveEssentialTask,
  deleteEssentialTask,
} from '../actions';
import { updateTaskCompletionStatus } from '../../overview/actions';
import { getApiStatus, toString } from '../../../core/utils';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { selectSite } from '../../../store/authReducer';
import UserGroupPermissions, {
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import {
  getLatestCompletionId,
  mapDisplayStatusToApiStatus,
  normalizeProjectStatus,
} from '../WorkOrders/QuickTasks/helpers';
import { TaskCompletionStatus } from '../types';
import { projectStatusOptions } from '../models';
import { unsnoozeTaskCompletion } from '../../overview/actions';
import { showDialog } from '../../../core/actions';
import { useSwipe } from '../../../core/hooks/useSwipe';
import { renderStatusPill } from '../WorkOrders/WorkOrderTable/constants';

const EssentialGuard = ({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactChild | React.ReactChildren;
}) =>
  active ? (
    <UserGroupPermissions itemRef={viewPermissionRefs.admin}>{children}</UserGroupPermissions>
  ) : (
    <>{children}</>
  );

interface TaskDetailsProps {
  taskId: number | null;
  isNew?: boolean;
  isEditing?: boolean;
  isEssential?: boolean;
  type?: number | null;
  onClose: any;
  onSave?: () => void;
  onDelete?: () => void;
}

export const TaskDetails = ({
  taskId,
  isNew = false,
  isEditing = false,
  isEssential = false,
  type,
  onClose,
  onSave,
  onDelete,
}: TaskDetailsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [task, setTask] = useState<Task | null>(null);
  const [latestCompletion, setLatestCompletion] = useState<TaskCompletion | null>(null);
  const [editing, setEditing] = useState(isNew || isEditing);
  const taskInEdit = useSelector(selectTaskInEdit);
  const currentEmployeeId = useSelector(selectCurrentEmployee);
  const siteId = useSelector(selectSite);
  const savingTask = useSelector((state) => getApiStatus(state, 'SAVE_TASK'));
  const isLoadingTask = useSelector((state) => getApiStatus(state, 'GET_TASK'));
  const deletingTask = useSelector(
    (state) => getApiStatus(state, 'DELETE_TASK') || getApiStatus(state, 'DELETE_ESSENTIAL_TASK')
  );
  const buttonsDisabled = isLoadingTask;
  const isEssentialTask = isEssential || !!task?.groupId;
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const drawerTransitionTime = 300;
  const closingRef = useRef<any | null>(null);

  const swipeRef = useSwipe<HTMLDivElement>({
    onSwipeRight: () => handleClose(),
  });

  const isProjectTask = (taskObj: Task | undefined) =>
    taskObj?.type === TaskType.Project || taskObj?.type === 1;

  const getDisplayStatus = (taskObj: Task | undefined): string => {
    if (!taskObj) return 'New';
    const raw = taskObj?.latestCompletion?.status;
    if (raw !== undefined && raw !== null) {
      const statusText =
        typeof raw === 'number' ? toString(TaskCompletionStatus, raw) : String(raw);
      return normalizeProjectStatus(statusText);
    }
    const desired = (taskObj as any).desiredStatus ?? (taskObj as any).desired_status;
    if (desired) return normalizeProjectStatus(desired);
    return 'New';
  };

  const handleStatusChange = (nextLabel: string) => {
    if (!task?.id) return;
    const employeeId = currentEmployeeId ?? (task as any).employeeId ?? (task as any).employee?.id;
    const site = Number(siteId);
    if (!employeeId || !site) {
      generateToastr(
        'error',
        intl.formatMessage({
          id: 'unableToChangeStatus',
          defaultMessage: 'Unable to change status',
        }),
        intl.formatMessage({
          id: 'selectEmployeeToUpdateStatus',
          defaultMessage: 'Select an employee before changing status.',
        })
      );
      return;
    }
    const completionId = getLatestCompletionId(task);
    if (!completionId) {
      generateToastr(
        'error',
        intl.formatMessage({
          id: 'unableToChangeStatus',
          defaultMessage: 'Unable to change status',
        }),
        intl.formatMessage({
          id: 'noActiveCompletionFound',
          defaultMessage:
            'No active task completion found for today. Open the task to manage status.',
        })
      );
      return;
    }
    setStatusDropdownOpen(false);
    const callback = () => {
      dispatch(getTask(taskId));
      onSave?.();
    };
    const common = {
      options: { siteId: site },
      taskIds: [completionId],
      employeeId,
      notes: null,
      status: mapDisplayStatusToApiStatus(nextLabel),
      callback,
    };
    dispatch(updateTaskCompletionStatus(common));
  };

  const defaultValues = {
    name: '',
    subTitle: '',
    description: null,
    locationId: '',
    start: moment(),
    weeklySchedule: moment().day().toString(),
    monthlySameWeekday: false,
    alertIfOverdue: true,
    type: type ?? TaskType.Maintenance,
    interval: type == TaskType.Project ? Interval.Once : Interval.CarCount,
    intervalCarCount: null,
    startingCarCount: null,
    intervalCount: 1,
    hourlySchedule: [],
    prefixId: 0,
    linkedTasks: [],
    projectStatus: 'Backlog',
    priority: 0,
    capacity: null,
    estimatedTime: null,
    employeeId: '',
  };

  const updateFormValues = (tsk: Task) => {
    return Object.keys(defaultValues).reduce((acc: any, key: string) => {
      const cloned: any = tsk;
      if (cloned && Object.keys(cloned).includes(key)) {
        if (cloned[key] !== null) {
          acc[key] = cloned[key]; // Update to task value if it exists
        }
      }
      return acc;
    }, {});
  };

  const form = useForm({ defaultValues: task ? updateFormValues(task) : defaultValues });

  const coerceNumber = (value: any) => {
    if (value && !Number.isNaN(value)) {
      return Number(value);
    }
    return null;
  };

  const handlePrimaryClick = () => {
    if (editing) {
      form.handleSubmit(onSubmit, onError)();
    } else {
      setEditing(true);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    if (closingRef.current) clearTimeout(closingRef.current);
    closingRef.current = setTimeout(() => {
      dispatch(deleteTaskInEdit());
      setEditing(false);
      setTask(null);
    }, drawerTransitionTime);
  };

  const handleDeleteTask = () => {
    if (!task?.id) return;
    const deletionParams = {
      id: task.id,
      callback: () => {
        handleClose();
        if (onDelete) onDelete();
      },
    };

    const confirmProps = {
      title: intl.formatMessage({ id: 'archiveTask' }),
      text: intl.formatMessage({ id: 'taskArchiveConfirmation' }, { name: task.name }),
      onReady: isEssentialTask
        ? () => dispatch(deleteEssentialTask(deletionParams))
        : () => dispatch(deleteTask(deletionParams)),
    };

    dispatch(showDialog('CONFIRM_DIALOG', confirmProps));
  };

  const onError = (values: any) => {
    generateToastr('error', 'Missing required form fields.', '', { timeOut: 1000 });
  };

  const blockTask = (t: Task) => {
    if (latestCompletion) {
      if (latestCompletion?.status != 'Snoozed') {
        dispatch(
          showDialog('SNOOZE_TASK', {
            taskCompletion: latestCompletion,
            onSnooze: onSave,
            dispatch: dispatch,
          })
        );
      } else {
        dispatch(
          unsnoozeTaskCompletion({
            employeeId: currentEmployeeId,
            taskIds: [latestCompletion?.id],
            notes: null,
            callback: [onSave, () => getTask(t?.id)],
          })
        );
      }
    }
  };

  const onSubmit = (values: any) => {
    //const isNew = !task?.id;
    const submitted = _.cloneDeep(values);

    //Add id to existing task submission data
    if (!isNew) {
      submitted.id = task?.id;
    }

    //Format locationId
    submitted.locationId = coerceNumber(submitted.locationId);

    //Format interval
    submitted.startingCarCount = coerceNumber(submitted.startingCarCount);
    submitted.intervalCarCount = coerceNumber(submitted.intervalCarCount) ?? 0;

    //Format start
    if (submitted?.start) {
      if (typeof submitted.start !== 'string') {
        submitted.start = submitted.start.toISOString();
      }
    } else {
      submitted.start = moment().startOf('day').toISOString();
    }

    //Format end (if exists)
    if (submitted?.end) {
      submitted.end = submitted.end.toISOString();
    }

    //Format / default priority
    submitted.priority = !isNaN(submitted.priority)
      ? Number(submitted.priority)
      : submitted.priority;

    //Format employeeId
    submitted.employeeId = coerceNumber(submitted?.employeeId);

    //Format capacity
    submitted.capacity = coerceNumber(submitted?.capacity);

    //Format estimated time
    submitted.estimatedTime = submitted?.estimatedTime ? String(submitted.estimatedTime) : null;

    //Format prefix
    if (submitted.type === TaskType.Project) {
      submitted.prefixId = coerceNumber(submitted.prefixId);
    } else {
      submitted.prefixId = null;
    }

    // Format videos
    submitted.videos = _.get(submitted, 'videos', []);
    const videoErrorCheck: any[] = [];
    if (submitted.videos.length) {
      _.compact(_.flatten(submitted.videos));
      submitted.videos = _.map(submitted.videos, (video: any) => {
        const videoCheck = checkVideoUrl(video);
        if (videoCheck === '' || videoCheck === undefined || videoCheck === 'Invalid Video URL') {
          videoErrorCheck.push(video);
        } else {
          return videoCheck;
        }
      });
    }
    if (videoErrorCheck.length) {
      _.map(videoErrorCheck, (video) => {
        generateToastr('error', 'Invalid Video URL:', video, { timeOut: 5000 });
      });
      return;
    }

    //Format linkedTasks
    submitted.linkedTasks = _.get(submitted, 'linkedTasks', []);
    if (submitted.linkedTasks.length) {
      submitted.linkedTasks = _.map(submitted.linkedTasks, (linkedTask) => {
        return { id: linkedTask.linkedTask.value, dependency: linkedTask.dependency.value };
      });
    }

    //Format inventory
    if (submitted.inventory && submitted.inventory.length > 0) {
      submitted.inventory.forEach((part: any) => {
        part.employeeId = part.employeeId || 0;
      });
    }

    //Send data to backend
    if (isNew) {
      if (isEssentialTask) {
        dispatch(addEssentialTask(submitted));
      } else {
        dispatch(addTask({ task: submitted, callback: onSave }));
      }
    } else {
      if (isEssentialTask) {
        if (task?.groupId) {
          dispatch(saveEssentialTask({ task: submitted }));
        } else {
          generateToastr('error', 'Error', 'Unable to save essential task');
        }
      } else {
        dispatch(saveTask({ task: submitted, callback: onSave }));
      }
    }
  };

  const components = {
    Instructions: <TaskInstructionsTab task={task} editing={editing} />,
    Details: <TaskDetailsTab task={task} type={type} editing={editing} />,
    'Linked Tasks': task ? <TaskLinkedTab task={task} editing={editing} /> : null,
    Notes: task ? <TaskDetailNotes task={task} /> : null,
    History: task ? <History task={task} /> : null,
    Media: task ? <TaskMediaTab task={task} /> : null,
  };

  useEffect(() => {
    if (taskId) {
      dispatch(getTask(taskId));
    } else {
      setTask(null);
    }
  }, [taskId]);

  useEffect(() => {
    setEditing(isNew || isEditing);
  }, [isNew, isEditing]);

  useEffect(() => {
    if (taskInEdit) {
      setTask(taskInEdit);
      if (taskInEdit?.latestCompletion) {
        setLatestCompletion(taskInEdit.latestCompletion);
      }
      setEditing(false);
    }
  }, [taskInEdit]);

  useEffect(() => {
    if (task) {
      form.reset(updateFormValues(task));
    } else {
      form.reset(defaultValues);
    }
  }, [task]);

  return (
    <div className="task-details-drawer" ref={swipeRef}>
      <div className="task-details-controls">
        <button className="button task-close" onClick={handleClose} disabled={buttonsDisabled}>
          <span>
            <IoClose />
          </span>
          <span>
            <MdChevronLeft />
            {intl.formatMessage({ id: 'back' })}
          </span>
        </button>
        <div className="task-actions">
          <EssentialGuard active={!!task?.groupId}>
            <>
              {task?.id && editing && (
                <button
                  type="button"
                  className="button primary inverted"
                  onClick={handleDeleteTask}
                  disabled={buttonsDisabled || deletingTask}
                >
                  {intl.formatMessage({ id: 'delete' })}
                </button>
              )}
              <ButtonWithLoader
                handleClick={handlePrimaryClick}
                isLoading={savingTask}
                disabled={buttonsDisabled}
                style="button primary"
              >
                <>
                  {editing
                    ? intl.formatMessage({ id: 'save' })
                    : intl.formatMessage({ id: 'edit' })}
                </>
              </ButtonWithLoader>
            </>
          </EssentialGuard>
        </div>
      </div>
      <div className="task-details-header">
        {editing ? (
          <>
            <div className="form-group padded">
              <label htmlFor="name">{intl.formatMessage({ id: 'taskName' })}</label>
              <Controller
                name="name"
                control={form.control}
                rules={{ required: true }}
                render={({ field }) =>
                  requiredInput({
                    input: field,
                    id: 'name',
                    type: 'text',
                    placeholder: 'Enter name for task',
                  })
                }
              />
            </div>
            <div className="form-group padded">
              <label htmlFor="type">{intl.formatMessage({ id: 'taskSubTitle' })}</label>
              <Controller
                name="subTitle"
                control={form.control}
                render={({ field }) => (
                  <Input
                    input={field}
                    id="subTitle"
                    type="text"
                    placeholder="Enter sub title for task"
                  />
                )}
              />
            </div>
          </>
        ) : (
          <>
            {task && (
              <>
                <h2>{task.name}</h2>
                {task?.subTitle && <p>{task.subTitle}</p>}
              </>
            )}
          </>
        )}
        {task?.interval === Interval.CarCount && (
          <>
            {task.forecast?.length ? (
              <div className="progress-label">
                <span>
                  {task.forecast[0].totalCarCount.toLocaleString('en-US')}/
                  {task.forecast[0].nextScheduledCarCount.toLocaleString('en-US')} vehicles
                </span>
              </div>
            ) : null}
          </>
        )}
        <div className="task-details-widgets">
          {task && (
            <>
              {latestCompletion && (
                <button
                  className={`button transparent snooze-button${
                    latestCompletion?.status == 'Snoozed' ? ' snoozed' : ''
                  }`}
                  onClick={() => blockTask(task)}
                >
                  <IoFlag />
                </button>
              )}
              {task?.id && isProjectTask(task) && (
                <Dropdown
                  isOpen={statusDropdownOpen}
                  toggle={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="task-status-dropdown"
                >
                  <DropdownToggle
                    className="button transparent task-status-button"
                    tag="button"
                    type="button"
                    disabled={buttonsDisabled}
                  >
                    {renderStatusPill(getDisplayStatus(task))}
                    <FaAngleDown />
                  </DropdownToggle>
                  <DropdownMenu>
                    {projectStatusOptions.map((opt) => (
                      <DropdownItem
                        key={opt}
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusDropdownOpen(false);
                          handleStatusChange(opt);
                        }}
                        active={getDisplayStatus(task) === opt}
                      >
                        {opt}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}
            </>
          )}
        </div>
      </div>
      <div className="task-details-tabs">
        <FormProvider {...form}>
          <ContentTabs
            intl={intl}
            components={components}
            scrollable={true}
            newStyle={true}
            defaultComponent={Number(isNew || editing)}
          />
        </FormProvider>
      </div>
    </div>
  );
};
